import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2MergeAction } from '@/lib/pr-v2/types';
import type { MergeMethod } from '@/lib/pull-requests/types';

import { isBlockedByBranchRules } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';

export function getMethodLabel(method: MergeMethod) {
  if (method === 'squash') {
    return 'Squash and merge';
  }
  if (method === 'rebase') {
    return 'Rebase and merge';
  }
  return 'Merge';
}

function getCapitalizedMergeMethodLabel(method: MergeMethod) {
  return method.charAt(0).toUpperCase() + method.slice(1);
}

/**
 * Auto-merge waits for approvals, required checks, and branch rules only GitHub
 * evaluates, such as code owner review. GitHub refuses it on a pull request that
 * can already merge or that is in a stack, and it cannot wait out merge conflicts.
 */
export function getCanQueueAutoMerge(status: MergeabilityStatus, isDraft: boolean) {
  return (
    !status.canMerge &&
    !isDraft &&
    status.stackPosition === null &&
    status.mergeConflicts.passed &&
    (!status.approvalStatus.passed ||
      !status.checksStatus.passed ||
      isBlockedByBranchRules(status)) &&
    status.autoMerge.canEnable &&
    status.viewer.canEnableAutoMerge
  );
}

/**
 * The method auto-merge was actually queued with, which is not necessarily the
 * viewer's preferred method — whoever enabled it may have chosen differently.
 */
function getQueuedAutoMergeMethod(status: MergeabilityStatus, fallback: MergeMethod): MergeMethod {
  return (status.autoMerge.enabledMethod?.toLowerCase() as MergeMethod | undefined) ?? fallback;
}

export type PrimaryMergeAction =
  | {
      kind: 'merge';
      mergeAction: PrV2MergeAction;
      mergeMethod: MergeMethod;
      /** How many pull requests the merge takes in when it merges a stack. */
      stackCount: number | null;
    }
  | { kind: 'auto-merge'; toggle: 'enable' | 'disable'; mergeMethod: MergeMethod }
  | { kind: 'view-queue'; url: string | null };

export type PrimaryButtonConfig = {
  text: string;
  disabled: boolean;
  /** Whether the picked merge method changes what the button does. */
  usesMergeMethod: boolean;
  action: PrimaryMergeAction;
};

export type PrimaryButtonConfigInput = {
  owner: string;
  repo: string;
  status: MergeabilityStatus;
  effectiveMergeMethod: MergeMethod;
  isPending: boolean;
  isAdminBypass: boolean;
  isDraft: boolean;
  canQueueAutoMerge: boolean;
};

export function getPrimaryButtonConfig({
  owner,
  repo,
  status,
  effectiveMergeMethod,
  isPending,
  isAdminBypass,
  isDraft,
  canQueueAutoMerge,
}: PrimaryButtonConfigInput): PrimaryButtonConfig {
  if (status.mergeQueue === 'queued') {
    // The queue page is per base branch, so there is nothing to open without one.
    const url = status.baseBranch
      ? `https://github.com/${owner}/${repo}/queue/${status.baseBranch}`
      : null;
    return {
      text: 'View merge queue',
      disabled: url === null,
      usesMergeMethod: false,
      action: { kind: 'view-queue', url },
    };
  }

  if (status.autoMerge.alreadyEnabled) {
    // GitHub ignores the method when disabling, but analytics records it — send
    // what was queued rather than this viewer's preference.
    const queuedMethod = getQueuedAutoMergeMethod(status, effectiveMergeMethod);
    return {
      text: `Disable auto-merge (${getCapitalizedMergeMethodLabel(queuedMethod)})`,
      disabled: isPending,
      // Nothing to pick once auto-merge is queued: the button disables it, and the queued
      // method is fixed until it is.
      usesMergeMethod: false,
      action: { kind: 'auto-merge', toggle: 'disable', mergeMethod: queuedMethod },
    };
  }

  const stackCount =
    status.stackPosition !== null && status.stackPosition > 1 ? status.stackPosition : null;
  const mergeLabel = `${getMethodLabel(effectiveMergeMethod)}${stackCount ? ' stack' : ''}`;
  // A merge queue also merges with its own method.
  const merge = (
    text: string,
    mergeAction: PrV2MergeAction,
    disabled: boolean,
  ): PrimaryButtonConfig => ({
    text,
    disabled,
    usesMergeMethod: mergeAction !== 'queue',
    action: { kind: 'merge', mergeAction, mergeMethod: effectiveMergeMethod, stackCount },
  });

  if (isAdminBypass) {
    // Merges directly, skipping a merge queue.
    return merge(`Bypass rules and ${mergeLabel}`, 'bypass', isPending || isDraft);
  }

  const isQueueRequired = status.mergeQueue === 'required';
  if (canQueueAutoMerge) {
    // A merge queue merges with its own method.
    return {
      text: isQueueRequired
        ? 'Enable auto-merge'
        : `Enable auto-merge (${getCapitalizedMergeMethodLabel(effectiveMergeMethod)})`,
      disabled: isPending,
      usesMergeMethod: !isQueueRequired,
      action: { kind: 'auto-merge', toggle: 'enable', mergeMethod: effectiveMergeMethod },
    };
  }

  const disabled = !status.canMerge || isPending || isDraft;
  return isQueueRequired
    ? merge(stackCount ? 'Add stack to merge queue' : 'Add to merge queue', 'queue', disabled)
    : merge(mergeLabel, 'direct', disabled);
}

/** One way to merge the pull request, as the command menu's merge picker lists it. */
export type PrV2MergeChoice = {
  id: string;
  label: string;
  /** The method the choice merges with, or null when the method changes nothing. */
  method: MergeMethod | null;
  action: PrimaryMergeAction;
  isPreferred: boolean;
  /** What to ask before a direct merge. Queueing and auto-merge run without asking. */
  confirmMessage: string | null;
};

function getMergeChoiceId(action: PrimaryMergeAction, method: MergeMethod | null) {
  const kind =
    action.kind === 'merge'
      ? action.mergeAction
      : action.kind === 'auto-merge'
        ? `${action.toggle}-auto-merge`
        : action.kind;
  return method ? `${kind}:${method}` : kind;
}

/**
 * Every action the merge dropdown's button could run right now, one per merge method. The admin
 * bypass stays behind the dropdown's checkbox: in the command menu, a search for "squash" would
 * otherwise offer to merge past the branch rules.
 */
export function getPrV2MergeChoices({
  number,
  selectableMergeMethods,
  ...input
}: Omit<PrimaryButtonConfigInput, 'isAdminBypass'> & {
  number: number;
  selectableMergeMethods: readonly MergeMethod[];
}): PrV2MergeChoice[] {
  const { baseBranch } = input.status;
  const toChoice = ({ text, action }: PrimaryButtonConfig, method: MergeMethod | null) => ({
    id: getMergeChoiceId(action, method),
    label: text,
    method,
    action,
    isPreferred: method === input.effectiveMergeMethod,
    confirmMessage:
      action.kind === 'merge' && action.mergeAction === 'direct'
        ? `${text} #${number}${baseBranch ? ` into ${baseBranch}` : ''}?`
        : null,
  });
  const preferred = getPrimaryButtonConfig({ ...input, isAdminBypass: false });
  // `disabled` never depends on the method, so one check covers every row.
  if (preferred.disabled) {
    return [];
  }
  // An action that ignores the method is the same for every method, so it is listed once, as the
  // dropdown shows it.
  if (!preferred.usesMergeMethod) {
    return [toChoice(preferred, null)];
  }

  // With no allowed methods known, the dropdown still merges with the effective one.
  const methods =
    selectableMergeMethods.length > 0 ? selectableMergeMethods : [input.effectiveMergeMethod];
  return methods.map((method) =>
    toChoice(
      getPrimaryButtonConfig({ ...input, effectiveMergeMethod: method, isAdminBypass: false }),
      method,
    ),
  );
}
